/**
 * Withdrawal requests.
 *
 * ─── This tab is disabled in preview builds ─────────────────────────────────
 * `WithdrawalController` is `@UseGuards(RolesGuard, PreviewDisabledGuard)`, so
 * in a preview build every call here answers 503 — including the list. Rather
 * than showing a table that fails to load with a message about service
 * availability, the tab says so up front and never calls. `isPreviewBuild()`
 * reads the same site-info the rest of the app gates on.
 *
 * ─── The two actions are not symmetrical ────────────────────────────────────
 * Approving writes an OUT invoice and a player event; rejecting returns the
 * money to the player's soft balance in the same transaction. Both are
 * irreversible from here — there is no un-approve endpoint — so both confirm,
 * and rejection collects the reason the row will carry from then on.
 */
import { approveWithdrawal, listWithdrawals, rejectWithdrawal } from '../admin-api';
import type { WithdrawalPage } from '../admin-api.types';
import { isPreviewBuild } from '../../../core/preview/preview';
import { badge, errorText, esc, fmtCents, fmtDate, pager, section, table } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  APPROVED: 'ok',
  PENDING: 'warn',
  REJECTED: 'bad',
};

export const withdrawalsTab: AdminTab = {
  id: 'withdrawals',
  label: 'Withdrawals',
  group: 'Billing',

  mount(host, ctx) {
    let live = true;
    let status = 'PENDING';
    let pageNo = 1;
    let data: WithdrawalPage | null = null;
    let error = '';

    if (isPreviewBuild()) {
      host.innerHTML = section(
        'Withdrawals',
        'Payout requests from players.',
        `<p class="tg-adm-empty">Withdrawals are switched off in preview builds.
          The endpoints behind this tab answer 503, so nothing is loaded.</p>`,
      );
      return () => {};
    }

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!data) return '<p class="tg-adm-empty">Loading…</p>';
      return `
        <div class="tg-adm-stats">
          <div class="tg-adm-stat${data.pendingTotalCents > 0 ? ' tg-adm-stat-attn' : ''}">
            <span class="tg-adm-stat-n">${esc(fmtCents(data.pendingTotalCents))}</span>
            <span class="tg-adm-stat-l">Waiting on approval</span>
          </div>
        </div>
        ${table(
    data.items,
    [
      {
        label: 'Player',
        cell: (r) => `<strong>${esc(r.user?.username || '—')}</strong><br>
          <span class="tg-adm-mono">${esc(r.user?.email ?? '')}</span>`,
      },
      { label: 'Amount', cell: (r) => esc(fmtCents(r.amountCents, r.currency)), tight: true },
      {
        label: 'Method',
        cell: (r) => esc(r.payoutDetails?.cardType || r.payoutMethod || '—'),
        tight: true,
      },
      {
        label: 'Status',
        cell: (r) => badge(r.status.toLowerCase(), STATUS_TONE[r.status] ?? 'mute')
              + (r.reason ? `<br><span class="tg-adm-hint">${esc(r.reason)}</span>` : ''),
        tight: true,
      },
      { label: 'Requested', cell: (r) => esc(fmtDate(r.createdAt)), tight: true },
      { label: 'Reviewed', cell: (r) => esc(fmtDate(r.reviewedAt)), tight: true },
      {
        label: '',
        tight: true,
        cell: (r) => (r.status !== 'PENDING' ? '' : `
          <div class="tg-adm-rowacts">
            <button type="button" class="tg-adm-btn" data-act="approve" data-id="${esc(r.id)}">Approve</button>
            <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-act="reject" data-id="${esc(r.id)}">Reject</button>
          </div>`),
      },
    ],
    'No requests with that status.',
  )}
        ${pager(data.page, data.pageSize, data.total)}`;
    }

    function paint(): void {
      host.innerHTML = section(
        'Withdrawals',
        'Payout requests. Approving raises an invoice; rejecting refunds the player.',
        body(),
        `<select data-status>
           <option value="PENDING">Pending</option>
           <option value="APPROVED">Approved</option>
           <option value="REJECTED">Rejected</option>
           <option value="">All</option>
         </select>`,
      );
      host.querySelector<HTMLSelectElement>('[data-status]')!.value = status;
    }

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listWithdrawals({ page: pageNo, status: status || undefined });
        if (!live) return;
        data = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function act(what: string, run: () => Promise<unknown>): Promise<void> {
      try {
        await run();
        if (!live) return;
        ctx.notify(what);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-page]');
      if (!el) return;
      if (el.dataset.page) { pageNo = Number(el.dataset.page); void load(); return; }
      const id = el.dataset.id!;
      if (el.dataset.act === 'approve') {
        ctx.confirm({
          title: 'Approve this payout?',
          body: 'An OUT invoice is raised against the account and the player is notified. There is no way to undo it from here.',
          confirmLabel: 'Approve',
          onConfirm: () => void act('Withdrawal approved.', () => approveWithdrawal(id)),
        });
      } else if (el.dataset.act === 'reject') {
        ctx.prompt({
          title: 'Reject this payout?',
          body: 'The amount goes straight back to the player\'s cash balance.',
          label: 'Reason',
          confirmLabel: 'Reject and refund',
          onConfirm: (reason) => void act('Withdrawal rejected and refunded.', () => rejectWithdrawal(id, reason)),
        });
      }
    };

    const onChange = (e: Event) => {
      const el = e.target as HTMLSelectElement;
      if (!el.hasAttribute('data-status')) return;
      status = el.value;
      pageNo = 1;
      void load();
    };

    host.addEventListener('click', onClick);
    host.addEventListener('change', onChange);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('change', onChange);
    };
  },
};
